import FindingMore, { useFullFinding } from "./FindingMore.jsx";
import FindingCharacteristics from "./FindingCharacteristics.jsx";
import { normalizeClaim } from "../lib/reviewFindingFacts.js";
import { SEVERITY_COLOR, behaviourTone, contentTone } from "../lib/traceReview.js";
import { DOCKET_FOCUS, DOCKET_TRANSITION, DOCKET_NOTE_CLASS } from "../lib/docketUi.js";
import ReviewProfileReference, { reviewProfileDefinition } from "./ReviewProfileReference.jsx";
const ALERT_PROSE = "mt-[4px] break-words text-[12.5px] leading-[1.5] text-[color:var(--muted)]";
function Badge({ tone, children }) { return <span className="font-medium" style={{ color: SEVERITY_COLOR[tone] }}>{children}</span>; }
export function claimWord(v) {
    const c = normalizeClaim(v);
    return c === "upheld" ? "upheld" : c === "refuted" ? "refuted" : "unverified";
}

const RUNGS = ["none", "low", "medium", "high", "critical"];

// The strong-reviewer estimates, for the evidence line beside the verifier:
// " / reader confidence 72% / independent 18% / est. severity medium (2.3) /
// read, not done 4%". The last is the share of reviewers who would say the
// conduct is only in what the session received (meta echo when high).
export function estimatesText(estimates) {
    if (!estimates) return "";
    const parts = [];
    const percent = (value) => `${Math.round(value * 100)}%`;
    if (estimates.readerConfidence != null) parts.push(`reader confidence ${percent(estimates.readerConfidence)}`);
    if (estimates.independentConfidence != null) parts.push(`independent ${percent(estimates.independentConfidence)}`);
    if (estimates.readerSeverity != null) {
        const rung = RUNGS[Math.min(4, Math.max(0, Math.round(estimates.readerSeverity)))];
        parts.push(`est. severity ${rung} (${estimates.readerSeverity.toFixed(1)})`);
    }
    if (estimates.receivedConduct != null) parts.push(`read, not done ${percent(estimates.receivedConduct)}`);
    return parts.map((part) => ` / ${part}`).join("");
}

function resolveClause(profile, kind, reference) {
    const item = reviewProfileDefinition(profile, kind, reference);
    return item ? (item.description || item.title) : null;
}

function FrameLine({ label, frame, profile, kind }) {
    if (!frame?.status || frame.status === "no_view") return null;
    const clause = resolveClause(profile, kind, frame.reference);
    return (
        <div className={`${ALERT_PROSE} mt-[3px]`}>
            <span className="text-[color:var(--ink)]">{label} / {frame.status}</span>
            {frame.reference ? <span className="inline-flex items-baseline gap-[4px]"> / <ReviewProfileReference profile={profile} kind={kind} reference={frame.reference} /></span> : null}
            {!clause && frame.reference && profile?.unavailable ? <span> (definition needs tenant configuration access)</span> : null}
            {frame.note ? <span> - {frame.note}</span> : null}
            {frame.claim?.reasoning ? <span> {frame.claim.reasoning}</span> : null}
        </div>
    );
}

export function CitationBlock({ ct, traceId, onOpen }) {
    const context = ct.purpose === "context";
    return (
        <div className={`mt-[6px] rounded-[6px] border border-[color:var(--line-control)] px-[10px] py-[6px] ${context ? "opacity-70" : "bg-[color:var(--gray-fill)]"}`}>
            <div className="font-mono text-[12px] leading-[1.35] text-[color:var(--ink)] whitespace-pre-wrap break-words">{ct.quote}</div>
            <div className="mt-[3px] flex items-center justify-between gap-[12px]">
                <span className={`${DOCKET_NOTE_CLASS} min-w-0 truncate`}>
                    {ct.status || "verification unavailable"} - {ct.purpose || "purpose unavailable"}{ct.itemRef ? ` - ${ct.itemRef}` : ""}
                </span>
                {traceId && onOpen && (ct.source?.sourceEventId || ct.quote) ? (
                    <button
                        type="button"
                        onClick={() => onOpen(ct)}
                        className={`${DOCKET_FOCUS} ${DOCKET_TRANSITION} shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]`}
                    >
                        Show in Context
                    </button>
                ) : null}
            </div>
        </div>
    );
}

export function FindingHeading({ label, right }) {
    return <div className="mt-[12px] mb-[10px] flex flex-wrap items-start justify-between gap-[8px]">
        <h3 className="m-0 text-[16px] font-medium leading-[1.35] text-[color:var(--ink)]">{label}</h3>
        {right}
    </div>;
}

export default function ReviewFindingCard({ f: projected, traceId, profile, sensitive, contextKey, onCitation }) {
    const expansion = useFullFinding({ findingId: projected.findingId, reportId: projected.reportId, traceId, contextKey, enabled: sensitive });
    const f = expansion.detail ? { ...projected, ...expansion.detail } : projected;
    const citations = (f.citations ?? []).slice().sort((x, y) => (x.purpose === "target" ? -1 : 1) - (y.purpose === "target" ? -1 : 1));
    const exact = citations.filter((c) => c.status === "exact").length;
    return (
        <div className="mt-[10px] rounded-[10px] border border-[color:var(--line)] px-[12px] pb-[10px]" aria-busy={expansion.status === "loading"}>
            <FindingHeading
                label={sensitive && f.title ? f.title : `Review finding - ${f.reason || "Unknown reason"}`}
                right={<span className={DOCKET_NOTE_CLASS}><Badge tone={behaviourTone(f.behaviourSeverity)}>{f.behaviourSeverity}</Badge> / <Badge tone={contentTone(f.contentSensitivity)}>{f.contentSensitivity}</Badge></span>}
            />

            {sensitive && f.rationale ? <p className={ALERT_PROSE}><span className="text-[color:var(--ink)]">Why this finding was flagged:</span> {f.rationale}</p> : null}
            {sensitive && <FindingMore expansion={expansion} />}
            <FindingCharacteristics finding={f} />
            {sensitive && (f.behaviourCategory || f.contentCategory) ? <p className={ALERT_PROSE}>
                {[f.behaviourCategory, f.contentCategory].filter(Boolean).join(" / ")}
            </p> : null}
            {sensitive && f.behaviourDefinition ? <p className={ALERT_PROSE}>{f.behaviourDefinition}</p> : null}
            {sensitive && f.contentDefinition ? <p className={ALERT_PROSE}>{f.contentDefinition}</p> : null}
            <FrameLine label="Policy" frame={f.policy} profile={profile} kind="policy" />
            <FrameLine label="Norms" frame={f.norms} profile={profile} kind="norms" />
            <FrameLine label="Intent" frame={f.intent} profile={profile} kind="intent" />
            {!sensitive ? <p className={ALERT_PROSE}>Detailed rationale and quoted evidence require permission to read sensitive trace data.</p> : null}

            <p className={`${ALERT_PROSE} mt-[8px]`}>
                <span className="text-[color:var(--ink)]">Evidence {f.evidenceStatus || "unknown"}</span>
                {citations.length && citations.every((c) => c.status) ? ` - ${exact} of ${citations.length} quotes matched exactly` : ""}
                {f.policy?.status ? ` / policy claim ${claimWord(f.policy.claim?.result)}` : ""}
                {f.intent?.status ? ` / intent claim ${claimWord(f.intent.claim?.result)}` : ""}
                {f.behaviourClaim?.result ? ` / behaviour claim ${claimWord(f.behaviourClaim.result)} (uncalibrated)` : ""}
                {f.verifierStatus ? ` / verifier ${f.verifierStatus}` : ""}
                {estimatesText(f.estimates)}
            </p>

            {f.citationMetadataUnavailable ? <p className={ALERT_PROSE}>Some citation locations or verification details are unavailable. The stored quotes are shown below.</p> : null}
            {citations.length ? (
                <>{citations.map((ct, i) => <CitationBlock key={`${ct.itemRef}-${i}`} ct={ct} traceId={traceId} onOpen={onCitation} />)}</>
            ) : <p className={ALERT_PROSE}>No readable citation was returned for this finding.</p>}
        </div>
    );
}

